import * as React from "react";

import { Avatar, Badge, Button, DescriptionList, IconButton } from "Components/ui";
import { Camera, Pencil } from "Components/ui/icons";
import { formatDate } from "utils/format";
import type { ProfileData } from "../data";
import DetailsForm from "./DetailsForm";
import PhotoPicker from "./PhotoPicker";

/**
 * Who you are signed in as: photo, name, role, and the username and email you sign in with.
 * The photo picker and the details form open in place, inside the drawer.
 */
const AccountSection: React.FC<{ profile: ProfileData }> = ({ profile }) => {
  const { user, photoSrc } = profile;
  const [editing, setEditing] = React.useState(false);
  const [choosingPhoto, setChoosingPhoto] = React.useState(false);
  if (!user) {
    return null;
  }
  const displayName = user.name || user.userName || "Your account";

  return (
    <section aria-label="Account" className="space-y-5">
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative">
          <Avatar name={displayName} src={photoSrc} size="xl" />
          <IconButton
            label={choosingPhoto ? "Close photo options" : "Change photo"}
            icon={<Camera />}
            size="sm"
            variant="secondary"
            aria-expanded={choosingPhoto}
            onClick={() => setChoosingPhoto((open) => !open)}
            className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full"
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-lg font-semibold tracking-tight text-foreground">
              {displayName}
            </h3>
            {user.role?.name && <Badge tone="primary">{user.role.name}</Badge>}
          </div>
          <p className="truncate text-sm text-muted-foreground">{user.email || user.userName}</p>
        </div>
        {!editing && (
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Pencil />}
            onClick={() => setEditing(true)}
          >
            Edit details
          </Button>
        )}
      </div>

      {choosingPhoto && <PhotoPicker profile={profile} onDone={() => setChoosingPhoto(false)} />}

      {editing ? (
        <DetailsForm profile={profile} onDone={() => setEditing(false)} />
      ) : (
        <DescriptionList
          items={[
            { label: "Name", value: user.name },
            { label: "Username", value: user.userName },
            { label: "Email", value: user.email },
            {
              label: "Member since",
              value: user.createdAt ? formatDate(user.createdAt) : null,
            },
          ]}
        />
      )}
    </section>
  );
};

export default AccountSection;
